/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

export function generateMarketCommentary(futuresSignals, optionsSignals) {
  if (!futuresSignals?.length || !optionsSignals?.length) {
    return "Insufficient data to generate market commentary.";
  }

  const getPrioritySignal = (signals) => {
    const priority = [
      "Long Build-up",
      "Short Build-up",
      "Short Covering",
      "Long Unwinding",
    ];

    const counts = {};
    signals.forEach(({ signal }) => {
      counts[signal] = (counts[signal] || 0) + 1;
    });

    return Object.entries(counts)
      .sort((a, b) => priority.indexOf(a[0]) - priority.indexOf(b[0]))
      [0][0];
  };

  const latestFutures = [...futuresSignals].sort(
    (a, b) => new Date(b.expiry) - new Date(a.expiry)
  )[0];
  const futuresView = getPrioritySignal(futuresSignals);

  const ceSignals = optionsSignals.filter((o) => o.option_type === "CE");
  const peSignals = optionsSignals.filter((o) => o.option_type === "PE");

  const ceView = getPrioritySignal(ceSignals);
  const peView = getPrioritySignal(peSignals);

  const commentary = [];

  commentary.push(
    `🧾 Futures Outlook: ${futuresView} seen in ${latestFutures.symbol} (Expiry: ${latestFutures.expiry}).`
  );

  if (ceView === peView) {
    commentary.push(
      `📊 Options Sentiment: Both Calls and Puts show "${ceView}", suggesting strong consensus in the options market.`
    );
  } else {
    commentary.push(
      `📉 Options Divergence: CE shows "${ceView}" while PE shows "${peView}", indicating mixed sentiment or hedging.`
    );
  }

  // Highlight significant strike actions
  const importantStrikes = optionsSignals.filter((o) =>
    ["Long Build-up", "Short Build-up"].includes(o.signal)
  );

  const topStrikes = importantStrikes.slice(0, 3); // Limit to top 3 for clarity
  if (topStrikes.length > 0) {
    commentary.push("🔍 Notable Strike Activity:");
    topStrikes.forEach((s) => {
      commentary.push(
        `→ ${s.option_type} at ${s.strike} strike shows "${s.signal}" (Expiry: ${s.expiry})`
      );
    });
  }

  // Final assessment
  if (futuresView.includes("Build-up")) {
    if (ceView.includes("Covering") || peView.includes("Unwinding")) {
      commentary.push(
        "⚠️ Caution: Options indicate profit booking or hesitation, despite directional build-up in futures."
      );
    } else {
      commentary.push("✅ Directional Bias: Market shows confirmation across Futures and Options.");
    }
  } else {
    commentary.push("🔄 Market seems to be consolidating or unwinding — wait for stronger signals.");
  }

  return commentary.join("\n");
}
